import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, FileUpload, Skeleton } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, Clock, FileText, XCircle } from 'lucide-react';
import { StatusBadge } from '@/components';
import { useMutation, useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';

const ICON = { verified: CheckCircle2, rejected: XCircle, pending: Clock, missing: FileText };
const EDITABLE = ['draft', 'info_requested'];
const STATUS_TEXT = {
  draft: 'Upload your documents, then submit the application.',
  submitted: 'Submitted. Our team reviews applications within 2 working days.',
  in_review: 'In review.',
  info_requested: 'We need something more from you — see the notes below, re-upload, and resubmit.',
  approved: 'Approved! Check your email for the link to set up your store admin account.',
  rejected: 'Your application was not approved. The reason is below.',
  paused: 'Your store is paused. The reason is below.',
};

async function uploadDoc(token, type, file) {
  const res = await fetch(`/api/platform/apply/${encodeURIComponent(token)}/documents/${type}`, {
    method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name || type) }, body: file,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Upload failed (${res.status})`);
  return body.data;
}

/** The applicant's private page (token link): KYC uploads, submit, status and reviewer notes. */
export default function ApplicationStatus() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const q = useQuery('sellerApplication', { token }, { select: (r) => r.data });
  const submit = useMutation('sellerApplicationSubmit');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const onFile = async (type, files) => {
    if (!files || !files.length) return;
    setErr('');
    setBusy(type);
    try {
      q.setData(await uploadDoc(token, type, files[0]));
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy('');
    }
  };
  const doSubmit = async () => {
    try {
      const r = await submit.run({ token });
      q.setData(r.data);
    } catch {
      /* shown below */
    }
  };

  const a = q.data;
  const lastReason = a ? [...a.trail].reverse().find((t) => t.reason && ['request_info', 'reject', 'pause'].includes(t.action)) : null;
  return (
    <div className="min-h-dvh bg-[var(--app-bg)] p-4 pt-safe pb-safe">
      <Card className="mx-auto max-w-2xl space-y-5 p-6">
        {q.error ? <Alert variant="error" title="This link can't be used">{q.error}</Alert> : !a ? <Skeleton count={5} /> : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h1 className="text-xl font-bold">{a.business_name}</h1>
                <p className="text-sm opacity-70">Application {a.id} · {a.email}</p>
              </div>
              <StatusBadge status={a.status} />
            </div>
            {params.get('new') ? <Alert variant="info">Bookmark this page: it is your private link to this application (we also emailed it).</Alert> : null}
            <p className="text-sm">{STATUS_TEXT[a.status] || a.status}</p>
            {lastReason ? <Alert variant={a.status === 'info_requested' ? 'warning' : 'error'}>{lastReason.reason}</Alert> : null}
            <ul className="divide-y divide-[var(--app-border)]">
              {a.documents.map((d) => {
                const Icon = ICON[d.status] || FileText;
                return (
                  <li key={d.type} className="space-y-2 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2 font-medium"><Icon size={18} aria-hidden="true" />{d.label}{d.required ? '' : ' (optional)'}</span>
                      <StatusBadge status={d.status === 'missing' ? 'pending' : d.status} label={d.status === 'missing' ? 'not uploaded' : d.status} />
                    </div>
                    {d.uploaded_at ? <p className="text-xs opacity-70">{d.file} · uploaded {formatDate(d.uploaded_at)}</p> : null}
                    {d.note ? <p className="text-sm text-error-600">{d.note}</p> : null}
                    {EDITABLE.includes(a.status) && d.status !== 'verified' ? (
                      <FileUpload accept="application/pdf,image/*" files={[]} onFileSelect={(fs) => onFile(d.type, fs)} maxSize={8 * 1024 * 1024} disabled={busy === d.type} />
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {err ? <Alert variant="error">{err}</Alert> : null}
            {submit.error ? <Alert variant="error">{submit.error}</Alert> : null}
            {EDITABLE.includes(a.status) ? <Button onClick={doSubmit} loading={submit.loading}>{a.status === 'draft' ? 'Submit application' : 'Resubmit'}</Button> : null}
          </>
        )}
      </Card>
    </div>
  );
}
